import { useApp } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { Card, ShowStatus } from '../../../shared';
import { timestampFormatter } from '../../../transforms';

function TransactionCard({ tx }) {
  return (
    <div className="rounded-md border border-black/10 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShowStatus state={tx.state} />
          <span className="text-black/60 text-xs">
            {tx.createdAt ? timestampFormatter(tx.createdAt) : 'N/A'}
          </span>
        </div>
        <span className="font-bold text-black text-sm">
          ${(tx.total / 100).toFixed(2)}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <dt className="font-bold text-black/60 text-xs">Status</dt>
          <dd className="text-black">{tx.status || 'N/A'}</dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Approved</dt>
          <dd className="text-black">{tx.amountApproved || 'N/A'}</dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Discount</dt>
          <dd className="text-black">
            ${(tx.discountAmount / 100).toFixed(2)}
          </dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Tax</dt>
          <dd className="text-black">${(tx.taxAmount / 100).toFixed(2)}</dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Shipping</dt>
          <dd className="text-black">
            ${(tx.shippingAmount / 100).toFixed(2)}
          </dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Auth Code</dt>
          <dd className="font-mono text-black text-xs">
            {tx.authcode || 'N/A'}
          </dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Transaction ID</dt>
          <dd className="font-mono text-black text-xs">
            {tx.transactionId || 'N/A'}
          </dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">AVS</dt>
          <dd className="text-black">{tx.avs || 'N/A'}</dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Code</dt>
          <dd className="font-mono text-black text-xs">{tx.code || 'N/A'}</dd>
        </div>
        <div>
          <dt className="font-bold text-black/60 text-xs">Message</dt>
          <dd className="text-black">{tx.message || 'N/A'}</dd>
        </div>
      </dl>
    </div>
  );
}

TransactionCard.propTypes = {
  tx: PropTypes.object.isRequired,
};

function OrderShowTransactions({ order }) {
  const { isAdmin } = useApp();

  if (!isAdmin) {
    return null;
  }

  if (!order.orderTransactions || order.orderTransactions.length === 0) {
    return (
      <Card className="p-4" heading="Transactions" wrapperClassName="mb-4 mt-6">
        <p className="mt-4 text-black/60 text-sm">No transactions recorded.</p>
      </Card>
    );
  }

  return (
    <Card className="p-4" heading="Transactions" wrapperClassName="mb-4 mt-6">
      {/* Mobile: card layout */}
      <div className="mt-4 space-y-3 lg:hidden">
        {order.orderTransactions.map((tx) => (
          <TransactionCard key={tx.id} tx={tx} />
        ))}
      </div>

      {/* Desktop: table layout */}
      <div className="mt-4 hidden overflow-x-auto lg:block">
        <table className="w-full text-black/80">
          <thead className="text-left text-black/80 text-sm">
            <tr>
              <th scope="col" className="py-2 pr-4 font-medium">
                Date
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                State
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Status
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Total
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Approved
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Discount
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Tax
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Shipping
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Transaction ID
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Auth Code
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                AVS
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Code
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Message
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/20 border-black/20 border-b text-sm sm:border-t">
            {order.orderTransactions.map((tx) => (
              <tr key={tx.id}>
                <td className="whitespace-nowrap py-3 pr-4">
                  {tx.createdAt ? timestampFormatter(tx.createdAt) : 'N/A'}
                </td>
                <td className="py-3 pr-4">
                  <ShowStatus state={tx.state} />
                </td>
                <td className="py-3 pr-4">{tx.status || 'N/A'}</td>
                <td className="whitespace-nowrap py-3 pr-4 text-right">
                  ${(tx.total / 100).toFixed(2)}
                </td>
                <td className="whitespace-nowrap py-3 pr-4 text-right">
                  {tx.amountApproved || 'N/A'}
                </td>
                <td className="whitespace-nowrap py-3 pr-4 text-right">
                  ${(tx.discountAmount / 100).toFixed(2)}
                </td>
                <td className="whitespace-nowrap py-3 pr-4 text-right">
                  ${(tx.taxAmount / 100).toFixed(2)}
                </td>
                <td className="whitespace-nowrap py-3 pr-4 text-right">
                  ${(tx.shippingAmount / 100).toFixed(2)}
                </td>
                <td className="py-3 pr-4 font-mono text-xs">
                  {tx.transactionId || 'N/A'}
                </td>
                <td className="py-3 pr-4 font-mono text-xs">
                  {tx.authcode || 'N/A'}
                </td>
                <td className="py-3 pr-4">{tx.avs || 'N/A'}</td>
                <td className="py-3 pr-4 font-mono text-xs">
                  {tx.code || 'N/A'}
                </td>
                <td className="py-3 pr-4">{tx.message || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

OrderShowTransactions.propTypes = {
  order: PropTypes.object.isRequired,
};

export default OrderShowTransactions;
